import React, { useState } from "react";
import {
  Icd10Codes,
  PatientContractDiagnosisCode,
  PatientContractDiagnosisCodeId,
  PatientContractDiagnosisCodeType,
  PatientContractDiagnosisSetParams,
} from "../../messages";
import { If } from "../Generic/If";
import AddAnotherPertinentDiagnosisButton from "./components/AddAnotherPertinentDiagnosisButton/AddAnotherPertinentDiagnosisButton";
import SinglePatientContractDiagnosisCodeActivity from "./SinglePatientContractDiagnosisCodeActivity";

interface Props {
  title: string;
  type: PatientContractDiagnosisCodeType | null;
  diagnoses: PatientContractDiagnosisCode[];
  isDisabled: boolean;
  isProfileSync: (code: PatientContractDiagnosisCode | null) => boolean;
  searchIcd: (searchValue: string) => Promise<Icd10Codes>;
  onCreate: (diagnosis: PatientContractDiagnosisSetParams, isProfileSync: boolean) => Promise<void>;
  onUpdate: (
    id: PatientContractDiagnosisCodeId,
    diagnosis: PatientContractDiagnosisSetParams,
    isProfileSync: boolean
  ) => Promise<void>;
  onDelete: (id: PatientContractDiagnosisCodeId, isProfileSync: boolean) => Promise<void>;
  onClickProfileSync: (code: PatientContractDiagnosisCode | null) => void;
}

const MultiplePatientContractDiagnosisCodeActivity = ({
  title,
  type,
  diagnoses,
  isDisabled,
  isProfileSync,
  searchIcd,
  onCreate,
  onUpdate,
  onDelete,
  onClickProfileSync,
}: Props) => {
  const [isNewDiagnosisOpen, setNewDiagnosisOpen] = useState(false);

  const handleCreate = React.useCallback(
    (diagnosis: PatientContractDiagnosisSetParams, synced: boolean) => {
      return onCreate(diagnosis, synced).then(() => setNewDiagnosisOpen(false));
    },
    [onCreate]
  );

  const handleUpdate = React.useCallback(
    (
      id: PatientContractDiagnosisCodeId,
      diagnosis: PatientContractDiagnosisSetParams,
      synced: boolean
    ) => {
      return onUpdate(id, diagnosis, synced).then(() => setNewDiagnosisOpen(false));
    },
    [onUpdate]
  );

  const handleDelete = React.useCallback(
    (id: PatientContractDiagnosisCodeId, synced: boolean) => {
      return onDelete(id, synced).then(() => setNewDiagnosisOpen(false));
    },
    [onDelete]
  );

  if (diagnoses.length === 0) {
    return (
      <SinglePatientContractDiagnosisCodeActivity
        title={title}
        type={type}
        diagnosis={null}
        isDisabled={isDisabled}
        searchIcd={searchIcd}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        isProfileSync={isProfileSync(null)}
        onClickProfileSync={onClickProfileSync}
      />
    );
  }

  return (
    <>
      {diagnoses.map((diagnosis) => (
        <SinglePatientContractDiagnosisCodeActivity
          title={title}
          key={diagnosis.id.toString()}
          type={type}
          diagnosis={diagnosis}
          isDisabled={isDisabled}
          searchIcd={searchIcd}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          isProfileSync={isProfileSync(diagnosis)}
          onClickProfileSync={onClickProfileSync}
        />
      ))}

      {isNewDiagnosisOpen ? (
        <SinglePatientContractDiagnosisCodeActivity
          title={title}
          type={type}
          diagnosis={null}
          isDisabled={isDisabled}
          searchIcd={searchIcd}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          isProfileSync={isProfileSync(null)}
          onClickProfileSync={onClickProfileSync}
        />
      ) : (
        <If condition={!isDisabled}>
          <AddAnotherPertinentDiagnosisButton onClick={() => setNewDiagnosisOpen(true)} />
        </If>
      )}
    </>
  );
};

export default React.memo(MultiplePatientContractDiagnosisCodeActivity);
